import React, { useEffect, useState } from 'react'
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material'
import {
  CheckCircle,
  Receipt,
  TrendingUp,
  Email,
  ArrowForward,
  AccessTime,
  Business,
  Public,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Helmet } from 'react-helmet-async'
import apiService from '../services/ApiService'

const CheckoutSuccess = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshQuota } = useAuth()

  const {
    itemType,
    itemName,
    amount,
    planCode,
    subscriptionId,
    creditsAdded,
    userSubscription: stateUserSubscription,
    completedAt,
  } = location.state || {}
  const [userSubscription, setUserSubscription] = useState(
    stateUserSubscription || null,
  )
  const [planDetails, setPlanDetails] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Refresh quota to reflect the new purchase
    refreshQuota()
  }, [refreshQuota])

  const toDate = (value) => {
    if (!value) return null

    if (Array.isArray(value)) {
      const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = value
      const date = new Date(
        year,
        month - 1,
        day,
        hour,
        minute,
        second,
        Math.floor((nano || 0) / 1000000),
      )
      return Number.isNaN(date.getTime()) ? null : date
    }

    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const formatDate = (value) => {
    const date = toDate(value)
    if (!date) return 'N/A'
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Fetch user subscription (preferred) + plan details (fallback/feature info)
  useEffect(() => {
    const fetchCheckoutDetails = async () => {
      if (itemType !== 'plan') {
        return
      }

      setLoading(true)

      try {
        const [historyResponse, planResponse] = await Promise.all([
          subscriptionId
            ? apiService
                .get('/api/v1/subscriptions/my-subscriptions/history')
                .catch((error) => {
                  console.error('Failed to fetch user subscription history:', error)
                  return null
                })
            : Promise.resolve(null),
          planCode
            ? apiService
                .get(`/api/v1/subscriptions/plans/code/${encodeURIComponent(planCode)}`)
                .catch((error) => {
                  console.error('Failed to fetch plan details:', error)
                  return null
                })
            : Promise.resolve(null),
        ])

        if (Array.isArray(historyResponse) && subscriptionId) {
          const matchedSubscription = historyResponse.find(
            (subscription) => subscription?.id === subscriptionId,
          )
          if (matchedSubscription) {
            setUserSubscription(matchedSubscription)
          }
        }

        if (planResponse) {
          setPlanDetails(planResponse)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchCheckoutDetails()
  }, [itemType, planCode, subscriptionId])

  // If no state data, redirect to pricing
  useEffect(() => {
    if (!itemType) {
      navigate('/pricing')
    }
  }, [itemType, navigate])

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(price)
  }

  // Determine plan type for customized messaging
  const getPlanType = () => {
    const source = userSubscription || planDetails
    if (!source) return null

    // Check if it's a 1-day plan (DurationType.DAILY)
    if (source.durationType === 'DAILY') {
      return 'daily'
    }

    // Check if it's DTAA-only plan (PlanType.DTAA_ONLY or dtaaOnly flag)
    if (source.dtaaOnly === true || source.planType === 'DTAA_ONLY') {
      return 'dtaa'
    }

    // Check if it's Enterprise plan (displayOrder = 6 or contains "Enterprise" in name)
    if (
      source.displayOrder === 6 ||
      (source.planName &&
        source.planName.toLowerCase().includes('enterprise')) ||
      (source.planCode &&
        source.planCode.toLowerCase().includes('enterprise'))
    ) {
      return 'enterprise'
    }

    return 'standard'
  }

  const planType = getPlanType()

  // Get plan-specific next steps
  const getNextSteps = () => {
    if (itemType === 'package') {
      return [
        'Your credits have been added to your account',
        'Credits never expire and can be used anytime',
        'Check your balance in the quota status banner',
        'Receipt has been sent to your email',
      ]
    }

    // Plan-specific next steps
    if (planType === 'daily') {
      return [
        'Your 24-hour access pass is now active',
        'Access unlimited documents for the next 24 hours',
        'Make the most of your access - explore all available content',
        'Receipt has been sent to your email',
      ]
    }

    if (planType === 'dtaa') {
      return [
        'Your DTAA-only subscription is now active',
        'Access unlimited Double Taxation Avoidance Agreements',
        'Use country comparison tools to analyze tax treaties',
        'Manage your subscription anytime from your profile',
        'Receipt has been sent to your email',
      ]
    }

    if (planType === 'enterprise') {
      return [
        'Your Enterprise subscription is now active',
        'Up to 7 team members can access the platform',
        'Your dedicated account manager will contact you shortly',
        'Access unlimited documents across all subscribed countries',
        'Centralized billing and administration available in your profile',
        'Receipt has been sent to your email',
      ]
    }

    // Standard subscription
    return [
      'Your subscription is now active and ready to use',
      'Access unlimited documents in your subscribed countries',
      (userSubscription?.includesDtaa ?? planDetails?.includesDtaa)
        ? 'Unlimited DTAA access included'
        : null,
      (userSubscription?.autoRenew ?? planDetails?.isRecurring)
        ? 'Subscription will auto-renew'
        : 'One-time access',
      'Manage your subscription anytime from your profile',
      'Receipt has been sent to your email',
    ].filter(Boolean)
  }

  const nextSteps = getNextSteps()

  // Get plan-specific features to display
  const getPlanFeatures = () => {
    if (!planDetails || itemType !== 'plan') return null

    return planDetails.features || []
  }

  const planFeatures = getPlanFeatures()

  // Get countries covered
  const getCountriesCovered = () => {
    if (userSubscription?.countries?.length) {
      return userSubscription.countries
    }
    if (planDetails?.countries?.length) {
      return planDetails.countries
    }
    return []
  }

  const countriesCovered = getCountriesCovered()
  const displayItemName = userSubscription?.planName || itemName
  const displayAmount = userSubscription?.amountPaid ?? amount
  const showAutoRenew = userSubscription?.autoRenew ?? planDetails?.isRecurring
  const startDate = userSubscription?.startDate
  const endDate = userSubscription?.endDate
  const detailRowSx = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: 0.5, sm: 2 },
    mb: 1.75,
  }

  const detailLabelSx = {
    fontSize: { xs: '1rem', sm: '1.05rem' },
    color: 'text.primary',
  }

  const detailValueSx = {
    fontWeight: 600,
    textAlign: { xs: 'left', sm: 'right' },
    width: { xs: '100%', sm: 'auto' },
    maxWidth: { sm: '68%' },
    overflowWrap: 'anywhere',
  }

  const planChipSx = {
    backgroundColor: '#232536',
    color: '#ffffff',
    fontWeight: 600,
    height: 'auto',
    maxWidth: '100%',
    py: 0.5,
    '& .MuiChip-label': {
      px: { xs: 1.5, sm: 2 },
      py: { xs: 0.35, sm: 0 },
      whiteSpace: 'normal',
      lineHeight: 1.3,
      textAlign: 'center',
    },
  }

  return (
    <>
      <Helmet>
        <title>Payment Successful | GCC Tax Laws</title>
        <meta name='robots' content='noindex, nofollow' />
      </Helmet>

      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: { xs: 4, sm: 5, md: 8 } }}>
        <Container maxWidth='md' sx={{ px: { xs: 1.25, sm: 2 } }}>
          {loading && itemType === 'plan' ? (
            <Paper
              sx={{
                p: { xs: 3, sm: 4, md: 6 },
                borderRadius: { xs: 2, sm: 3 },
                textAlign: 'center',
              }}
            >
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading subscription details...</Typography>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: { xs: 2.5, sm: 4, md: 6 },
                borderRadius: { xs: 2, sm: 3 },
                textAlign: 'center',
                boxShadow: 3,
              }}
            >
              {/* Success Icon */}
              <Box
                sx={{
                  width: { xs: 72, sm: 88, md: 100 },
                  height: { xs: 72, sm: 88, md: 100 },
                  borderRadius: '50%',
                  bgcolor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: { xs: '0 auto 18px', sm: '0 auto 24px' },
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: { xs: 42, sm: 54, md: 60 },
                    color: '#43af76',
                  }}
                />
              </Box>

              {/* Success Message */}
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.25, sm: 2 },
                  color: '#232536',
                  fontSize: { xs: '2rem', sm: '2.4rem', md: '3rem' },
                }}
              >
                Payment Successful!
              </Typography>

              <Typography
                variant='h6'
                sx={{
                  mb: { xs: 3, sm: 4 },
                  color: 'text.secondary',
                  fontWeight: 400,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  lineHeight: 1.5,
                }}
              >
                {planType === 'daily'
                  ? 'Thank you for your purchase! Your 24-hour access pass is now active.'
                  : planType === 'enterprise'
                    ? 'Thank you for your purchase! Your Enterprise subscription is now active for your team.'
                    : planType === 'dtaa'
                      ? 'Thank you for your purchase! Your DTAA-only subscription is now active.'
                      : `Thank you for your purchase. Your ${
                          itemType === 'plan' ? 'subscription' : 'credits'
                        } ${itemType === 'plan' ? 'is' : 'are'} now active.`}
              </Typography>

              {/* Plan Type Badge */}
              {planType && (
                <Box
                  sx={{
                    mb: { xs: 2.5, sm: 3 },
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  {planType === 'daily' && (
                    <Chip
                      icon={<AccessTime style={{ color: '#ffffff' }} />}
                      label='24-Hour Access Pass'
                      sx={planChipSx}
                    />
                  )}
                  {planType === 'dtaa' && (
                    <Chip
                      icon={<Public style={{ color: '#ffffff' }} />}
                      label='DTAA-Only Subscription'
                      sx={planChipSx}
                    />
                  )}
                  {planType === 'enterprise' && (
                    <Chip
                      icon={<Business style={{ color: '#ffffff' }} />}
                      label='Enterprise Subscription'
                      sx={planChipSx}
                    />
                  )}
                </Box>
              )}

              {/* Purchase Details */}
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: { xs: 3, sm: 4 },
                  bgcolor: '#f9f9f9',
                  borderRadius: 2,
                  textAlign: 'left',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Receipt sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    Purchase Details
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={detailRowSx}
                >
                  <Typography variant='body1' sx={detailLabelSx}>
                    Item:
                  </Typography>
                  <Typography variant='body1' sx={detailValueSx}>
                    {displayItemName}
                  </Typography>
                </Box>

                <Box
                  sx={detailRowSx}
                >
                  <Typography variant='body1' sx={detailLabelSx}>
                    Type:
                  </Typography>
                  <Typography variant='body1' sx={detailValueSx}>
                    {itemType === 'plan' ? 'Subscription' : 'Credit Package'}
                  </Typography>
                </Box>

                {/* Show countries covered for plans */}
                {itemType === 'plan' && countriesCovered.length > 0 && (
                    <Box
                      sx={detailRowSx}
                    >
                      <Typography variant='body1' sx={detailLabelSx}>
                        Countries:
                      </Typography>
                      <Typography variant='body1' sx={detailValueSx}>
                        {countriesCovered.length === 6 &&
                        countriesCovered.includes('UAE') &&
                        countriesCovered.includes('KSA')
                          ? 'All GCC Countries'
                          : countriesCovered.join(', ')}
                      </Typography>
                    </Box>
                  )}

                {/* Show duration for plans */}
                {itemType === 'plan' && (userSubscription || planDetails) && (
                  <Box
                    sx={detailRowSx}
                  >
                    <Typography variant='body1' sx={detailLabelSx}>
                      Duration:
                    </Typography>
                    <Typography variant='body1' sx={detailValueSx}>
                      {(userSubscription?.durationType || planDetails?.durationType) ===
                      'DAILY'
                        ? '24 Hours'
                        : (userSubscription?.durationType || planDetails?.durationType) ===
                            'MONTHLY'
                          ? '1 Month'
                          : (userSubscription?.durationType || planDetails?.durationType) ===
                              'ANNUAL'
                            ? '12 Months'
                            : userSubscription?.durationType ||
                              planDetails?.durationType ||
                              'N/A'}
                      {showAutoRenew && ' (Auto-renewal enabled)'}
                    </Typography>
                  </Box>
                )}

                {itemType === 'plan' && startDate && (
                  <Box sx={detailRowSx}>
                    <Typography variant='body1' sx={detailLabelSx}>
                      Start Date:
                    </Typography>
                    <Typography variant='body1' sx={detailValueSx}>
                      {formatDate(startDate)}
                    </Typography>
                  </Box>
                )}

                {itemType === 'plan' && endDate && (
                  <Box sx={detailRowSx}>
                    <Typography variant='body1' sx={detailLabelSx}>
                      {showAutoRenew ? 'Renewal Date:' : 'End Date:'}
                    </Typography>
                    <Typography variant='body1' sx={detailValueSx}>
                      {formatDate(endDate)}
                    </Typography>
                  </Box>
                )}

                {/* Show credits for packages */}
                {itemType === 'package' && creditsAdded && (
                  <Box
                    sx={detailRowSx}
                  >
                    <Typography variant='body1' sx={detailLabelSx}>
                      Credits Added:
                    </Typography>
                    <Typography variant='body1' sx={detailValueSx}>
                      {creditsAdded} credits
                    </Typography>
                  </Box>
                )}

                {/* Show subscription ID if available */}
                {subscriptionId && (
                  <Box
                    sx={detailRowSx}
                  >
                    <Typography variant='body1' sx={detailLabelSx}>
                      Subscription ID:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        ...detailValueSx,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: 'text.secondary',
                      }}
                    >
                      {subscriptionId.substring(0, 8)}...
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={detailRowSx}
                >
                  <Typography variant='body1' sx={detailLabelSx}>
                    Amount Paid:
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{
                      ...detailValueSx,
                      fontWeight: 700,
                      color: '#232536',
                      fontSize: { xs: '2rem', sm: '2.1rem' },
                    }}
                  >
                    {formatPrice(displayAmount)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...detailRowSx,
                    mb: 0,
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                  >
                    Date:
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      textAlign: { xs: 'left', sm: 'right' },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {formatDate(completedAt || new Date())}
                  </Typography>
                </Box>
              </Paper>

              {/* Plan Features (if available) */}
              {planFeatures && planFeatures.length > 0 && (
                <Box sx={{ textAlign: 'left', mb: { xs: 3, sm: 4 } }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.75rem', sm: '2rem' } }}
                  >
                    What's Included:
                  </Typography>
                  <List>
                    {planFeatures.map((feature, index) => (
                      <ListItem
                        key={index}
                        sx={{ px: 0, py: { xs: 0.75, sm: 1 }, alignItems: 'flex-start' }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 42 }, mt: '2px' }}>
                          <CheckCircle
                            sx={{ color: '#232536', fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            sx: { lineHeight: 1.45, fontSize: { xs: '1rem', sm: '1.05rem' } },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Next Steps */}
              <Box sx={{ textAlign: 'left', mb: { xs: 3, sm: 4 } }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.75rem', sm: '2rem' } }}
                >
                  What's Next?
                </Typography>
                <List>
                  {nextSteps.map((step, index) => (
                    <ListItem
                      key={index}
                      sx={{ px: 0, py: { xs: 0.75, sm: 1 }, alignItems: 'flex-start' }}
                    >
                      <ListItemIcon sx={{ minWidth: { xs: 36, sm: 42 }, mt: '2px' }}>
                        <CheckCircle sx={{ color: '#232536' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={step}
                        primaryTypographyProps={{
                          sx: { lineHeight: 1.45, fontSize: { xs: '1rem', sm: '1.05rem' } },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  width: '100%',
                  maxWidth: 620,
                  mx: 'auto',
                }}
              >
                <Button
                  variant='contained'
                  size='large'
                  fullWidth
                  startIcon={<TrendingUp />}
                  onClick={() => navigate('/')}
                  sx={{
                    bgcolor: '#232536',
                    color: '#ffcf51',
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    minHeight: 56,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    '&:hover': {
                      bgcolor: '#3a3e5a',
                    },
                  }}
                >
                  Start Exploring
                </Button>

                <Button
                  variant='outlined'
                  size='large'
                  fullWidth
                  startIcon={<ArrowForward />}
                  onClick={() => navigate('/profile?tab=subscriptions')}
                  sx={{
                    borderColor: '#232536',
                    color: '#232536',
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    minHeight: 56,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    '&:hover': {
                      borderColor: '#1a1c2e',
                      bgcolor: '#f5f5f5',
                    },
                  }}
                >
                  View Subscriptions
                </Button>
              </Box>

              {/* Email Notice */}
              <Box
                sx={{
                  mt: { xs: 3, sm: 4 },
                  p: { xs: 2, sm: 2.25 },
                  bgcolor: '#e3f2fd',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  textAlign: 'center',
                }}
              >
                <Email sx={{ color: '#1976d2' }} />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ lineHeight: 1.5, fontSize: { xs: '0.98rem', sm: '1rem' } }}
                >
                  A confirmation email with your receipt has been sent to your
                  registered email address.
                </Typography>
              </Box>
            </Paper>
          )}
        </Container>
      </Box>
    </>
  )
}

export default CheckoutSuccess
